import { Link } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import Logo from "../components/Logo";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { DISPLAY_DOMAIN, SITE_URL } from "../lib/brand";

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <div className="max-w-4xl mx-auto px-6 py-10">
        <Link to="/" className="inline-flex items-center gap-2 text-blue-700 hover:text-blue-900 text-sm mb-6">
          <ArrowLeft size={16} /> Back to Home
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <Logo />
          <span className="text-xs uppercase tracking-widest text-slate-500">EchoSense Capital Club</span>
        </div>
        <h1 className="text-4xl font-serif font-semibold text-slate-900 mb-1">Privacy Policy</h1>
        <p className="text-sm text-slate-500 mb-8">Last updated: February 2026</p>

        <Card className="bg-white border-slate-200">
          <CardContent className="p-6 md:p-8 space-y-6 text-slate-800 leading-relaxed text-[15px]">

            <section>
              <p>
                EchoSense Capital Club ("EchoSense", "we", "us", "our") respects the privacy of every visitor,
                prospective member, member, introducer, and approved regulated partner. This Privacy Policy describes
                what information we collect, how we use it, with whom we share it, and the rights you have over your
                personal information.
              </p>
              <p className="mt-3">
                EchoSense is a private membership, verification, and regulated-partner routing portal. EchoSense does
                not provide investment advice, discuss returns, receive client funds, issue funding instructions, or
                approve investment participation.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-2">1. Information we collect</h2>
              <p>We collect information you provide directly to us, including:</p>
              <ul className="list-disc ml-6 mt-2 space-y-1">
                <li><strong>Account information:</strong> name, email, password (hashed), phone number, mailing address.</li>
                <li><strong>Onboarding information:</strong> answers to capital readiness, capital safety, and introducer-statement intake questions.</li>
                <li><strong>Identity documents (KYC):</strong> government-issued ID, proof of address, accredited investor verification documents, processed through approved KYC providers.</li>
                <li><strong>Payment information:</strong> processed and stored by Stripe; we receive only confirmation status, last 4 digits of payment instrument, and the Stripe customer reference.</li>
                <li><strong>Communications:</strong> messages you send to us via email, SMS, phone (including voicemails and transcriptions), or the in-portal messaging system.</li>
                <li><strong>Device & usage information:</strong> IP address, browser type, device type, pages visited, referral source (including introducer VC#).</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-2">2. How we use your information</h2>
              <ul className="list-disc ml-6 space-y-1">
                <li>Verify your identity and accredited / qualified investor status.</li>
                <li>Route eligible files to approved regulated partners (with your written consent).</li>
                <li>Track introducer / referral chains and attribute compliant introducer participation.</li>
                <li>Send transactional emails and SMS related to your file status, KYC milestones, appointment confirmations, and compliance notices.</li>
                <li>Respond to your inquiries and provide customer support.</li>
                <li>Detect and prevent fraud, abuse, money laundering, terrorist financing, and prohibited conduct.</li>
                <li>Comply with legal obligations, court orders, and regulatory requests.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-2">3. SMS &amp; phone communications</h2>
              <p>
                When you provide your phone number to EchoSense — either at registration, in your onboarding intake,
                or by texting our published number — you consent to receive transactional SMS messages from
                <strong> EchoSense Capital Club</strong> related to your account, your onboarding status, appointment
                reminders, security verification, and compliance notices.
              </p>
              <p className="mt-3">
                <strong>SMS frequency varies.</strong> Message and data rates may apply. Carriers are not liable for
                delayed or undelivered messages. You may reply <strong>STOP</strong> at any time to opt out of further
                SMS, or <strong>HELP</strong> for assistance. EchoSense does not share, sell, or rent mobile phone
                numbers or SMS opt-in data to third parties or affiliates for marketing or promotional purposes.
              </p>
              <p className="mt-3">
                For full SMS terms and the opt-in disclosure, see our{" "}
                <Link to="/sms-terms" className="text-blue-700 hover:underline">SMS Terms &amp; Opt-In Policy</Link>.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-2">4. How we share your information</h2>
              <p>We do not sell, rent, or trade your personal information. We share information only with:</p>
              <ul className="list-disc ml-6 mt-2 space-y-1">
                <li><strong>Approved regulated partners</strong> — only after you sign the data transfer consent in the portal.</li>
                <li><strong>KYC / accredited investor verification providers</strong> — to perform identity and eligibility checks.</li>
                <li><strong>Payment processors (Stripe)</strong> — to process membership fees.</li>
                <li><strong>Communications providers (Twilio, Microsoft 365)</strong> — to deliver SMS, voice, and email.</li>
                <li><strong>Legal &amp; regulatory authorities</strong> — when required by law, subpoena, or court order.</li>
                <li><strong>Professional advisors (legal, accounting, compliance)</strong> — under confidentiality obligations.</li>
              </ul>
              <p className="mt-3">
                <strong>Mobile information and SMS opt-in data are never shared with third parties for marketing or
                promotional purposes.</strong>
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-2">5. Data retention</h2>
              <p>
                We retain personal information for as long as your account is active, plus the period required to
                comply with applicable financial, anti-money-laundering, tax, and regulatory record-keeping obligations
                (generally 5–7 years after account closure).
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-2">6. Security</h2>
              <p>
                We use industry-standard encryption (TLS for data in transit, hashed passwords, encrypted secrets),
                access controls, audit logs, and segregated production data stores. No method of transmission over the
                internet is 100% secure; we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-2">7. Your rights</h2>
              <p>Depending on your jurisdiction, you may have the right to:</p>
              <ul className="list-disc ml-6 mt-2 space-y-1">
                <li>Access, correct, or delete your personal information.</li>
                <li>Restrict or object to certain processing.</li>
                <li>Receive a copy of your data in a portable format.</li>
                <li>Withdraw consent to data transfer to a regulated partner (prior to transfer).</li>
                <li>Opt out of SMS by replying STOP.</li>
              </ul>
              <p className="mt-3">
                To exercise any of these rights, contact{" "}
                <a href="mailto:admin@echosensecapital.com" className="text-blue-700 hover:underline">admin@echosensecapital.com</a>.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-2">8. Children</h2>
              <p>
                EchoSense is not directed to individuals under the age of 18. We do not knowingly collect personal
                information from minors. If we discover that we have collected information from a minor, we will delete
                it promptly.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-2">9. International data transfers</h2>
              <p>
                EchoSense operates from the United States. If you access the portal from outside the United States,
                your information may be transferred to, stored, and processed in the United States. By using EchoSense,
                you consent to this transfer.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-2">10. Changes to this Privacy Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. Material changes will be communicated by email or
                in-portal notice. The "Last updated" date at the top reflects the most recent revision.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-2">11. Contact</h2>
              <p>
                EchoSense Capital Club<br />
                <a href="mailto:admin@echosensecapital.com" className="text-blue-700 hover:underline">admin@echosensecapital.com</a><br />
                <a href="tel:+18336573746" className="text-blue-700 hover:underline">+1 (833) 657-3746</a><br />
                <a href={SITE_URL} className="text-blue-700 hover:underline">{DISPLAY_DOMAIN}</a>
              </p>
            </section>

            <div className="pt-4 border-t border-slate-200 flex items-center gap-2 text-sm text-emerald-700">
              <ShieldCheck size={16} /> EchoSense does not sell or share your information for third-party marketing.
            </div>
          </CardContent>
        </Card>

        <div className="mt-8 text-center">
          <Button variant="outline" onClick={() => window.print()}>Print this Policy</Button>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
